import React from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { track, Form as ReduxForm, Fieldset } from 'react-redux-form';
import { Button, Col, Card, Icon, Row, Collapse, message } from 'antd';
import { translate } from 'react-i18next';
import { withRouter } from 'react-router-dom';
import { updateProfileProperties, submitProfile, resetSiretValidation, resetSubmitted } from 'modules/profile';

import FormSchema from 'components/Form/FormSchema';
import Input from 'components/Fields/Input';
import Select from 'components/Fields/Select';
import Checkbox from 'components/Fields/Checkbox';
import withAuthentication from 'hoc/authentication';
import ProfileContact from './ProfileContact';
import options from './Profile.config';

import styles from './Profile.module.scss';

const { Panel } = Collapse;

const customPanelStyle = {
  background: '#f7f7f7',
  borderRadius: 4,
  marginBottom: 12,
  border: 0,
  overflow: 'hidden',
};

const formItemLayout = {
  gutter: 16,
};

class Profile extends React.Component {
  static propTypes = {
    onSubmit: PropTypes.func,
  };

  static defaultProps = {
    onSubmit () {},
  };

  componentDidMount () {
    this.props.updateProfileProperties();
  }

  componentDidUpdate (prevProps) {
    const { profile, structureType } = this.props;
    if (profile.submitted && !profile.submitFailed) {
      message.success('Votre profil a été mis à jour');
    }

    if (structureType !== prevProps.structureType) {
      this.props.resetSiretValidation();
    }
  }

  getSiretValidation () {
    const { structureType } = this.props;

    const validators = {
      format: {
        message: 'Le numéro SIRET n\'est pas valide',
        rule: v => !v || (v.match(/^[0-9]{14}$/)),
      },
    };
    if (['entrepreneur individuel', 'société commerciale'].includes(structureType)) {
      validators.required = { message: 'Le numéro SIRET est obligatoire' };
    }

    return validators;
  }

  /**
   * Remove the contact by its id
   * @param  {number} k
   */
  removeContact = uid => this.props.updateProfileProperties({
    contacts: this.props.profile.properties.contacts.filter(contact => contact.uid !== uid),
  });

  /**
   * Add and init a new contact
   */
  addContact = () => {
    const { contacts } = this.props.profile.properties;
    const keys = contacts && contacts.map(contact => contact.uid);
    const uid = contacts ? Math.max(...keys) + 1 : 0;

    this.props.updateProfileProperties({
      structure: this.props.profile.properties.structure,
      contacts: [
        ...this.props.profile.properties.contacts || [],
        {
          gender: '',
          firstname: '',
          lastname: '',
          position: '',
          zipcode: '',
          city: '',
          phone: '',
          email: '',
          uid,
        },
      ],
    });
  }


  formSubmit = () => {
    const {
      profile: { properties },
      user: { email },
      onSubmit,
    } = this.props;
    onSubmit(properties);
    this.props.submitProfile(email, properties);
  }

  setSubmitted = () => {
    this.props.resetSubmitted();
  }

  render () {
    const { profile, form, t } = this.props;
    const { formSubmit, setSubmitted } = this;
    return (
      <ReduxForm
        model="profile"
        onSubmit={formSubmit}
        onChange={setSubmitted}
      >
        <Card title={t('Structure')} style={{ marginTop: 24 }}>
          <Fieldset model=".properties.structure">
            <Row {...formItemLayout}>
              <Col span={24} className={styles.Col}>
                <Input model=".name" label={t('Nom/Raison sociale')} errorMessages={{ required: { message: t('Le nom est obligatoire') } }} />
              </Col>
            </Row>
            <Row {...formItemLayout} style={{ paddingBottom: 0 }}>
              <Col span={24} sm={16} className={styles.Col}>
                <Select
                  className={styles.Select}
                  label="Catégorie juridique"
                  model=".type"
                  placeholder="Saisissez ou sélectionnez un type de structure"
                  options={options}
                  errorMessages={{ required: { message: t('Le type de structure est obligatoire') } }}
                  showSearch
                  dropdownMatchSelectWidth
                />
              </Col>
              <Col span={24} sm={8} className={styles.Col}>
                <Input
                  model=".siret"
                  label={t('Numéro SIRET')}
                  errorMessages={this.getSiretValidation()}
                />
              </Col>
            </Row>

            <Row {...formItemLayout}>
              <Col span={24} className={styles.Col}>
                <Input model=".address.street" label={t('Adresse')} errorMessages={{ required: { message: t('L\'adresse est obligatoire') } }} />
              </Col>
            </Row>

            <Row {...formItemLayout} style={{ paddingBottom: 24 }}>
              <Col span={24} sm={12} className={styles.Col}>
                <Input model=".address.zipcode" label={t('Code postal')} errorMessages={{ required: { message: t('Le code postal est obligatoire') } }} />
              </Col>
              <Col span={24} sm={12} className={styles.Col}>
                <Input model=".address.city" label={t('Ville')} errorMessages={{ required: { message: t('La ville est obligatoire') } }} />
              </Col>
            </Row>

            <Row {...formItemLayout} style={{ paddingBottom: 24 }}>
              <Col span={24} className={styles.Col}>
                <Checkbox
                  model=".affiliation"
                  label={t('Votre structure est-elle rattachée à une ou plusieurs fédérations suivantes ?')}
                  options={FormSchema.affiliation}
                />
              </Col>
            </Row>

            <Row {...formItemLayout}>
              <Col span={24} className={styles.Col}>
                <Input
                  model=".label"
                  label={t("Disposez-vous d'un label qualité ou d'une convention de partenariat avec l'ONF ?")}
                  placeholder={t('Nom du label')}
                />
              </Col>
            </Row>
          </Fieldset>
        </Card>
        <Card title={t('Représentant légal habilité à engager la structure')} style={{ marginTop: 24 }}>
          {profile.properties.contacts && profile.properties.contacts.map((contact, i) => (
            i === 0
            && (

            <Fieldset model={track('.properties.contacts[]', item => item.uid === contact.uid)} key={contact.uid}>
              <ProfileContact contact={contact} defaultPosition="Représentant légal" disabled={() => (true)} />
            </Fieldset>
            )
          ))}
        </Card>
        {profile.properties.contacts.length >= 2
          ? (
            <Card title={t('Contacts')} style={{ marginTop: 24 }}>
              <p>Veuillez renseigner une ou plusieurs personnes contacts.</p>
              <p>Le contact 1 sera celui contacté par défaut par l’ONF
              pour tout sujet en lien avec une activité de la structure
              </p>
              {profile.properties.contacts && profile.properties.contacts.map((contact, i) => (
                i >= 1 && (
                <Collapse defaultActiveKey={['1']} bordered={false} style={{ marginBottom: 24 }} key={contact.uid}>
                  <Panel
                    forceRender
                    header={(
                      <div style={{ display: 'flex', marginRight: 12 }}>
                        <span style={{ flex: 1 }}>{`Contact ${i}`}</span>
                        {profile.properties.contacts.length > 1 && (
                        <Button
                          size="small"
                          type="danger"
                          style={{ textAlign: 'right' }}
                          onClick={() => this.removeContact(contact.uid)}
                        >
                          <Icon type="delete" /> {t('Supprimer ce contact')}
                        </Button>
                        )}
                      </div>
                    )}
                    style={customPanelStyle}
                    key={contact.uid}
                  >
                    <Fieldset model={track('.properties.contacts[]', item => item.uid === contact.uid)} key={contact.uid}>
                      <ProfileContact contact={contact} defaultPosition={contact.uid === 1 ? 'Contact de terrain' : ''} disabled={!(contact.uid >= 2)} />
                    </Fieldset>
                  </Panel>
                </Collapse>
                )))}
              <Button type="dashed" onClick={this.addContact} style={{ margin: '24px auto' }}>
                <Icon type="plus" /> {t('Ajouter une personne de contact')}
              </Button>
            </Card>
          )
          : (
            <div style={{ marginTop: 24 }}>
              <Button
                type="secondary"
                icon="plus"
                onClick={this.addContact}
              >
                {t('Ajouter des contacts')}
              </Button>
            </div>

          )
        }
        <div style={{ marginTop: 24, textAlign: 'right' }}>
          <Button
            type="primary"
            htmlType="submit"
            icon="arrow-right"
            loading={form.pending}
            disabled={!form.valid}
          >
            {t('Enregistrer')}
          </Button>
        </div>
      </ReduxForm>
    );
  }
}


const mapStateToProps = state => ({
  profile: state.profile,
  form: state.forms.profile.$form,
  structureType: state.profile.properties.structure.type,
  structureSiret: state.profile.properties.structure.siret,
});

const mapDispatchToProps = dispatch => bindActionCreators({
  updateProfileProperties,
  submitProfile,
  resetSiretValidation,
  resetSubmitted,
}, dispatch);

export default
withRouter(withAuthentication(connect(
  mapStateToProps,
  mapDispatchToProps,
)(translate('profileTranslations')(Profile))));



// WEBPACK FOOTER //
// ./src/components/Profile/User/Profile.js